import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import ReactTable from 'react-table';

import { t } from '../../../../modules/infra/i18n';
import Date from '../../../common/date';
import Money from '../../../common/money';
import Pagination from '../../../common/Pagination';
import SubscriptionActions from './SubscriptionActions';
import { SUBSCRIPTIONS_LIST_PAGE_SIZE } from './SubscriptionsListContainer';

const getSubscriptionStatuses = () => ({
  active: {
    label: t('memberships:subscriptions_list.status.active'),
    value: 'active',
  },
  pastDue: {
    label: t('memberships:subscriptions_list.status.past_due'),
    value: 'past_due',
  },
  canceled: {
    label: t('memberships:subscriptions_list.status.canceled'),
    value: 'canceled',
  },
  paused: {
    label: t('memberships:subscriptions_list.status.paused'),
    value: 'paused',
  },
});

const SubscriptionsList = (props) => (
  <div className="u-width-100">
    <div className="o-flexbox o-flexbox--justify-end u-mb+ u-mt">
      <select className="c-select u-max-width-300" onChange={props.handleStatusChange} value={props.activeStatus}>
        <option value="all">{t('memberships:subscriptions_list.all_statuses_option')}</option>
        {Object.values(getSubscriptionStatuses()).map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
    </div>
    <ReactTable
      className="c-table-new c-no-hover c-table-paginated u-mb"
      data={props.activePageItems}
      minRows={0}
      defaultPageSize={SUBSCRIPTIONS_LIST_PAGE_SIZE}
      showPagination={false}
      resizable={false}
      noDataText={t('memberships:common.no_rows_found')}
      getTrProps={(state, row) => {
        const isDisabled = props.pendingSubscriptions[row.original.id];
        return {
          className: cx({
            'u-opacity': isDisabled,
            'no-pointer-events': isDisabled,
          }),
        };
      }}
      columns={[
        {
          Header: t('memberships:subscriptions_list.table.member_name_header'),
          accessor: 'subscriberName',
          id: 'subscriberName',
        },
        {
          Header: t('memberships:subscriptions_list.table.package_name_header'),
          accessor: 'membershipsName',
          id: 'membershipsName',
        },
        {
          Header: t('memberships:subscriptions_list.table.shop_name_header'),
          accessor: (invoice) => props.shopNamesById[invoice.ownerId] || '',
          id: 'shopName',
          show: props.showShopColumn,
        },
        {
          Header: t('memberships:subscriptions_list.table.started_on_header'),
          accessor: 'startDate',
          id: 'startDate',
          Cell: (row) => <Date>{row.value}</Date>,
        },
        {
          Header: t('memberships:subscriptions_list.table.next_billing_header'),
          accessor: 'nextBillingDate',
          id: 'nextBillingDate',
          Cell: (row) => (row.value ? <Date>{row.value}</Date> : t('memberships:common.not_available')),
        },
        {
          Header: t('memberships:subscriptions_list.table.lifetime_revenue_header'),
          accessor: 'lifetimeRevenue',
          id: 'lifetimeRevenue',
          Cell: (row) => (row.value ? <Money>{row.value}</Money> : t('memberships:common.not_available')),
        },
        {
          Header: t('memberships:subscriptions_list.table.status_header'),
          accessor: 'status',
          id: 'status',
          Cell: (row) => (
            <div className={`c-memberships-status c-memberships-status__${row.value}`}>
              {t(`memberships:status.${row.value}`)}
            </div>
          ),
        },
        {
          Header: t('memberships:subscriptions_list.table.actions_header'),
          id: 'actions',
          Cell: (row) => <SubscriptionActions performAction={props.performAction} subscription={row.original} />,
        },
      ]}
    />
    <div className="c-heading c-heading--small u-mt" display-if={!props.activePageItems.length}>
      {t('memberships:common.no_rows_found')}
    </div>
    <Pagination
      display-if={props.totalPages > 1}
      onPageChange={props.openPage}
      pageCount={props.totalPages}
      currentPage={props.activePage}
    />
  </div>
);

SubscriptionsList.propTypes = {
  activePageItems: PropTypes.array.isRequired,
  totalPages: PropTypes.number.isRequired,
  activePage: PropTypes.number.isRequired,
  openPage: PropTypes.func.isRequired,
  handleStatusChange: PropTypes.func.isRequired,
  activeStatus: PropTypes.string.isRequired,
  performAction: PropTypes.func.isRequired,
  pendingSubscriptions: PropTypes.object.isRequired,
  showShopColumn: PropTypes.bool.isRequired,
  shopNamesById: PropTypes.object.isRequired,
};

export default SubscriptionsList;
